import React, { useEffect, useState, useMemo } from 'react';
import { MEAL_PRESETS } from '../../components/guests/addressStatus';
import AppIcon from '../../components/AppIcon';
import AutoDeclineStatus from '../../components/rsvp/AutoDeclineStatus';
import client from '../../api/client';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import InfoTip from '../../components/InfoTip';
import { Loader2, RotateCcw, ExternalLink, Copy, Check, X, Plus, Trash2, Link2, Mail, MessageSquare, Monitor, AlertTriangle } from 'lucide-react';
import PhotoUploader from '../../components/PhotoUploader';
import { coupleNames as fmtCouple } from '../../utils/coupleNames';
import DigestReportsEditor from './DigestReportsEditor';

// Small pill that clearly labels where each editable message shows up
// (on the RSVP web page, in a confirmation email, in a confirmation SMS,
// etc). Prevents the confusion the couple had between the on-page
// thank-you screen and the confirmation SMS.
function ChannelPill({ channel }) {
  const map = {
    page:  { icon: Monitor,        label: 'Shown on RSVP page',        cls: 'bg-[#F4E4DA] text-[#7B2C3B] border-[#EBD3C2]' },
    email: { icon: Mail,           label: 'Sent by email',              cls: 'bg-[#E8EEF0] text-[#3E5C76] border-[#D0DCE2]' },
    sms:   { icon: MessageSquare,  label: 'Sent by SMS',                cls: 'bg-[#E8F0E8] text-[#4A7A4A] border-[#D0DEC9]' },
  };
  const cfg = map[channel] || map.page;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

// The dashboard's "RSVP" tab. Left column = editor for the per-couple
// rsvp_config. Right column = a phone-shaped live preview that mirrors
// exactly what a guest sees at /rsvp/<token> — driven by the same config
// object being edited, so tweaks reflect instantly with no reload.

export default function RsvpEditor() {
  const [cfg, setCfg] = useState(null);
  const [couple, setCouple] = useState(null);
  const [previewGuest, setPreviewGuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedForGuest, setCopiedForGuest] = useState('');
  const [guests, setGuests] = useState([]);
  const [issuingLinks, setIssuingLinks] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [pv, gs] = await Promise.all([
          client.get('/rsvp-config/preview'),
          client.get('/guests'),
        ]);
        setCfg(pv.data.config);
        setCouple(pv.data.couple);
        setPreviewGuest(pv.data.guest);
        setGuests(gs.data || []);
      } catch (e) {
        toast({ title: 'Could not load RSVP settings', description: e?.response?.data?.detail || e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await client.put('/rsvp-config', patch);
      setCfg(data);
      window.dispatchEvent(new Event('rsvp-config:saved'));
    } catch (e) {
      toast({ title: 'Could not save', description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  // Reply-by date lives on the couple's profile (it also gates the email
  // {replyByLine} merge field), so persist there and mirror into `couple`
  // so the phone-preview updates instantly.
  const saveProfile = async (patch) => {
    setSaving(true);
    try {
      const { data } = await client.put('/profile', patch);
      setCouple((c) => ({ ...(c || {}), ...data }));
      window.dispatchEvent(new Event('rsvp-config:saved'));
    } catch (e) {
      toast({ title: 'Could not save', description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset the RSVP page to the default questions and copy?')) return;
    setSaving(true);
    try {
      const { data } = await client.post('/rsvp-config/reset');
      setCfg(data);
      toast({ title: 'RSVP page reset to defaults' });
    } catch (e) {
      toast({ title: 'Reset failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="flex items-center justify-center py-24 text-[#6A6A6A]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div data-testid="rsvp-editor" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl text-[#2A2A2A] flex items-center gap-3"><AppIcon feature="rsvp" size="lg" reveal testId="section-icon-rsvp" />RSVP page</h1>
          <p className="text-sm text-[#6A6A6A] mt-1">Customize exactly what your guests see on their RSVP link. Changes below appear instantly in the preview on the right.</p>
        </div>
        <div className="flex items-center gap-2">
          {saving && <span className="text-xs text-[#6A6A6A] inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> saving…</span>}
          <button data-testid="rsvp-reset" onClick={reset} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C] inline-flex items-center gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-6 items-start">
        {/* -------- Editor (left) -------- */}
        <div className="space-y-5">
          {/* Welcome message */}
          <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                Welcome message
                <InfoTip testId="info-welcome">
                  <p>Shown right at the top of the RSVP web page, just above the reply buttons.</p>
                  <p>Merge fields: <code>{'{firstName}'}</code>, <code>{'{coupleNames}'}</code></p>
                </InfoTip>
              </h3>
              <ChannelPill channel="page" />
            </div>
            <textarea
              data-testid="rsvp-cfg-welcome"
              rows={2}
              value={cfg.welcomeMessage}
              onChange={(e) => setCfg({ ...cfg, welcomeMessage: e.target.value })}
              onBlur={(e) => save({ welcomeMessage: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
            />
          </section>

          {/* Reply button labels */}
          <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                Reply buttons
                <InfoTip testId="info-reply-buttons">
                  <p>The heading and two big buttons on the RSVP page. There&apos;s no &quot;Maybe&quot; — guests either accept or decline. They can come back and update their answer anytime.</p>
                  <p>Formal wording tip: &quot;Happily Accepts&quot; and &quot;Regretfully Declines&quot; feel very wedding-appropriate.</p>
                </InfoTip>
              </h3>
              <ChannelPill channel="page" />
            </div>
            <div className="mb-3">
              <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Attendance question</label>
              <input
                data-testid="rsvp-cfg-attendance-question"
                value={cfg.attendanceQuestion || ''}
                placeholder="Will you be joining us?"
                onChange={(e) => setCfg({ ...cfg, attendanceQuestion: e.target.value })}
                onBlur={(e) => save({ attendanceQuestion: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
              />
              <p className="text-[10px] text-[#9A8A7E] mt-1">Shown right above the two reply buttons.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Attending button</label>
                <input
                  data-testid="rsvp-cfg-attending-label"
                  value={cfg.attendingLabel || ''}
                  placeholder="Happily Accepts"
                  onChange={(e) => setCfg({ ...cfg, attendingLabel: e.target.value })}
                  onBlur={(e) => save({ attendingLabel: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Declining button</label>
                <input
                  data-testid="rsvp-cfg-declining-label"
                  value={cfg.decliningLabel || ''}
                  placeholder="Regretfully Declines"
                  onChange={(e) => setCfg({ ...cfg, decliningLabel: e.target.value })}
                  onBlur={(e) => save({ decliningLabel: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                />
              </div>
            </div>
          </section>

          {/* Reply-by date */}
          <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                Reply by date
                <InfoTip testId="info-reply-by">
                  <p>Shown large and italic right under the big <strong>RSVP</strong> button in the invitation email (e.g. &quot;Kindly reply by October 18, 2026&quot;). Available as merge field <code>{'{replyByDate}'}</code>. Leave blank to fall back to a softer &quot;Kindly reply at your earliest convenience&quot;.</p>
                </InfoTip>
              </h3>
              <ChannelPill channel="email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3 items-start">
              <div>
                <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Reply-by date</label>
                <input
                  type="date"
                  data-testid="profile-reply-by-date"
                  value={couple?.replyByDate || ''}
                  onChange={(e) => setCouple({ ...(couple || {}), replyByDate: e.target.value })}
                  onBlur={(e) => saveProfile({ replyByDate: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                />
              </div>
              <div className="text-xs text-[#6A6A6A] mt-6 sm:mt-6">
                Renders on the invite as: <span className="italic text-[#2A2A2A]">
                  {couple?.replyByDate
                    ? `Kindly reply by ${new Date(couple.replyByDate + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`
                    : 'Kindly reply at your earliest convenience'}
                </span>
              </div>
            </div>
          </section>

          {/* Venue photo — optional; displayed below the venue address on the RSVP page. */}
          <section data-testid="rsvp-cfg-venue-photo-section" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                Venue photo
                <InfoTip testId="info-venue-photo">
                  <p>Optional. Upload a photo of your venue and it&apos;ll appear on the RSVP page just below the venue address — so guests recognize the spot before they arrive.</p>
                  <p>16:9 crop looks best. Landscape wins over portrait.</p>
                </InfoTip>
              </h3>
              <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C] bg-white cursor-pointer">
                <input
                  data-testid="rsvp-cfg-venue-photo-show"
                  type="checkbox"
                  checked={cfg.showVenuePhoto !== false}
                  onChange={(e) => { setCfg({ ...cfg, showVenuePhoto: e.target.checked }); save({ showVenuePhoto: e.target.checked }); }}
                  className="accent-[#7B2C3B] w-3.5 h-3.5"
                />
                Show on RSVP page
              </label>
            </div>
            <PhotoUploader
              testId="rsvp-cfg-venue-photo"
              label="Venue photo"
              aspect="wide"
              value={couple?.venuePhoto || ''}
              onChange={async (url) => {
                try {
                  await client.put('/profile', { venuePhoto: url || '' });
                  setCouple((c) => ({ ...(c || {}), venuePhoto: url || '' }));
                  toast({ title: url ? 'Venue photo updated' : 'Venue photo removed' });
                } catch (e) {
                  toast({ title: 'Could not save venue photo', description: e?.response?.data?.detail || e.message });
                }
              }}
            />
            {!couple?.venuePhoto && (
              <p className="text-[11px] text-[#9A8A7E] mt-2">No photo yet — upload a landscape shot of the venue for a friendlier RSVP page.</p>
            )}
          </section>

          {/* Which questions show + per-field required flag */}
          <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
              Which questions to show
              <InfoTip testId="info-sections">
                <p>Toggle any of the standard RSVP questions off if you don&apos;t need them.</p>
                <p>The <strong>Required</strong> column controls whether the guest must fill that field in before they can submit their RSVP.</p>
                <p><strong>Plus one</strong> is only shown to guests you&apos;ve flagged as plus-one-eligible on the Guests tab — this toggle just lets you hide the section entirely.</p>
              </InfoTip>
            </h3>
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-1 items-center">
              <span></span>
              <span className="text-[10px] uppercase tracking-wider text-[#9A8A7E] text-center">Show</span>
              <span className="text-[10px] uppercase tracking-wider text-[#9A8A7E] text-center">Required</span>
              {[
                ['plusOne', 'Plus one', false],
                ['meal', 'Meal preference', true],
                ['dietary', 'Dietary needs / allergies', true],
                ['address', 'Mailing address', true],
                ['phone', 'Phone number', true],
                ['notes', '"Anything else" notes', true],
                ['customQuestion', 'Custom question (write your own)', true],
              ].map(([k, label, canRequire]) => (
                <React.Fragment key={k}>
                  <span className="text-sm text-[#2A2A2A] py-1.5">{label}</span>
                  <label className="flex justify-center py-1.5">
                    <input
                      data-testid={`rsvp-cfg-section-${k}`}
                      type="checkbox"
                      checked={!!cfg.sections?.[k]}
                      onChange={(e) => {
                        const next = { ...(cfg.sections || {}), [k]: e.target.checked };
                        setCfg({ ...cfg, sections: next });
                        save({ sections: { [k]: e.target.checked } });
                      }}
                      className="accent-[#7B2C3B] w-4 h-4"
                    />
                  </label>
                  <label className="flex justify-center py-1.5">
                    {canRequire ? (
                      <input
                        data-testid={`rsvp-cfg-required-${k}`}
                        type="checkbox"
                        disabled={!cfg.sections?.[k]}
                        checked={!!cfg.required?.[k]}
                        onChange={(e) => {
                          const next = { ...(cfg.required || {}), [k]: e.target.checked };
                          setCfg({ ...cfg, required: next });
                          save({ required: { [k]: e.target.checked } });
                        }}
                        className="accent-[#7B2C3B] w-4 h-4 disabled:opacity-40"
                      />
                    ) : (
                      <span className="text-[10px] text-[#9A8A7E]">—</span>
                    )}
                  </label>
                </React.Fragment>
              ))}
            </div>
          </section>

          {/* Plus-one copy customizer */}
          {cfg.sections?.plusOne && (
            <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
              <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
                Plus-one question
                <InfoTip testId="info-plus-one">
                  <p>Only guests flagged as plus-one-eligible on the Guests tab will ever see this question. Everyone else&apos;s RSVP form skips it — that keeps your headcount accurate.</p>
                  <p>Guests answer <strong>Yes</strong> or <strong>No</strong>. On Yes, the &quot;name&quot; field appears. On No, we drop them from the plus-one headcount automatically.</p>
                </InfoTip>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Question</label>
                  <input
                    data-testid="rsvp-cfg-plusone-question"
                    value={cfg.plusOne?.question || ''}
                    onChange={(e) => setCfg({ ...cfg, plusOne: { ...(cfg.plusOne || {}), question: e.target.value } })}
                    onBlur={(e) => save({ plusOne: { question: e.target.value } })}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Help text (shown below the question)</label>
                  <input
                    data-testid="rsvp-cfg-plusone-help"
                    value={cfg.plusOne?.helpText || ''}
                    onChange={(e) => setCfg({ ...cfg, plusOne: { ...(cfg.plusOne || {}), helpText: e.target.value } })}
                    onBlur={(e) => save({ plusOne: { helpText: e.target.value } })}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Guest-name label (shown after "Yes")</label>
                  <input
                    data-testid="rsvp-cfg-plusone-name-label"
                    value={cfg.plusOne?.guestNameLabel || ''}
                    onChange={(e) => setCfg({ ...cfg, plusOne: { ...(cfg.plusOne || {}), guestNameLabel: e.target.value } })}
                    onBlur={(e) => save({ plusOne: { guestNameLabel: e.target.value } })}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Guest-name placeholder</label>
                  <input
                    data-testid="rsvp-cfg-plusone-name-placeholder"
                    value={cfg.plusOne?.guestNamePlaceholder || ''}
                    onChange={(e) => setCfg({ ...cfg, plusOne: { ...(cfg.plusOne || {}), guestNamePlaceholder: e.target.value } })}
                    onBlur={(e) => save({ plusOne: { guestNamePlaceholder: e.target.value } })}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                  />
                </div>
              </div>
            </section>
          )}

          {/* Meal options */}
          {cfg.sections?.meal && (
            <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                  Meal options
                  <InfoTip testId="info-meal">
                    <p><strong>Choice</strong>: guests pick from your dropdown.</p>
                    <p><strong>Preset menu</strong>: no dropdown — you're serving the same meal to everyone. A short note takes the place of the dropdown so guests know.</p>
                  </InfoTip>
                </h3>
                <div className="inline-flex rounded-full bg-[#F4E4DA] p-0.5 text-xs">
                  {[['choice', 'Choice'], ['preset', 'Preset menu']].map(([id, label]) => (
                    <button
                      key={id}
                      data-testid={`rsvp-cfg-meal-mode-${id}`}
                      onClick={() => { setCfg({ ...cfg, mealMode: id }); save({ mealMode: id }); }}
                      className={`px-3 py-1 rounded-full transition-colors ${cfg.mealMode === id ? 'bg-[#7B2C3B] text-white' : 'text-[#7B2C3B]'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {cfg.mealMode === 'choice' && (
                <MealOptionsEditor
                  options={cfg.mealOptions || []}
                  onChange={(opts) => { setCfg({ ...cfg, mealOptions: opts }); save({ mealOptions: opts }); }}
                />
              )}
              {cfg.mealMode === 'preset' && (
                <div>
                  <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Note guests see</label>
                  <textarea
                    data-testid="rsvp-cfg-meal-preset-note"
                    rows={2}
                    value={cfg.mealPresetNote}
                    onChange={(e) => setCfg({ ...cfg, mealPresetNote: e.target.value })}
                    onBlur={(e) => save({ mealPresetNote: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                  />
                </div>
              )}
            </section>
          )}

          {/* Custom question */}
          {cfg.sections?.customQuestion && (
            <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
              <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
                Your custom question
                <InfoTip testId="info-custom-question"><p>A freeform question you write — e.g. song requests, dance floor prompts, kids attending, transportation needs.</p></InfoTip>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Label</label>
                  <input
                    data-testid="rsvp-cfg-custom-label"
                    value={cfg.customQuestion?.label || ''}
                    onChange={(e) => setCfg({ ...cfg, customQuestion: { ...(cfg.customQuestion || {}), label: e.target.value } })}
                    onBlur={(e) => save({ customQuestion: { label: e.target.value } })}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Placeholder / hint</label>
                  <input
                    data-testid="rsvp-cfg-custom-placeholder"
                    value={cfg.customQuestion?.placeholder || ''}
                    onChange={(e) => setCfg({ ...cfg, customQuestion: { ...(cfg.customQuestion || {}), placeholder: e.target.value } })}
                    onBlur={(e) => save({ customQuestion: { placeholder: e.target.value } })}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
                  />
                </div>
              </div>
            </section>
          )}

          {/* Email field behavior */}
          <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
              Email address field
              <InfoTip testId="info-email-field">
                <p>The RSVP form always shows an email input. It's pre-filled from the guest list when we have it.</p>
                <p>When enabled, it becomes <strong>required</strong> for any guest we don't have an email for yet — that's how you'll capture the ones you're still missing.</p>
                <p>Once a guest submits their email through the RSVP form, we automatically send them the full wedding invitation email.</p>
              </InfoTip>
            </h3>
            <label className="flex items-center justify-between gap-3 p-2 rounded-lg border border-[#EFE3D8] hover:bg-[#FBF7F2]">
              <span className="text-sm text-[#2A2A2A]">Require email if we don&apos;t have one on file</span>
              <input
                data-testid="rsvp-cfg-email-required"
                type="checkbox"
                checked={cfg.emailField?.requiredIfMissing !== false}
                onChange={(e) => {
                  const next = { ...(cfg.emailField || {}), requiredIfMissing: e.target.checked };
                  setCfg({ ...cfg, emailField: next });
                  save({ emailField: { requiredIfMissing: e.target.checked } });
                }}
                className="accent-[#7B2C3B] w-4 h-4"
              />
            </label>
          </section>

          {/* Submit button label */}
          <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-3">Submit button</h3>
            <input
              data-testid="rsvp-cfg-submit-label"
              value={cfg.submitLabel}
              onChange={(e) => setCfg({ ...cfg, submitLabel: e.target.value })}
              onBlur={(e) => save({ submitLabel: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
            />
          </section>

          {/* Thank-you */}
          <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
                Thank-you screen
                <InfoTip testId="info-thanks">
                  <p>The final screen guests see on the RSVP web page right after they hit submit. This is <strong>not</strong> the email or SMS they get — those are edited further down.</p>
                  <p>Merge fields: <code>{'{firstName}'}</code>, <code>{'{coupleNames}'}</code></p>
                </InfoTip>
              </h3>
              <ChannelPill channel="page" />
            </div>
            <textarea
              data-testid="rsvp-cfg-thanks"
              rows={2}
              value={cfg.thankYouMessage}
              onChange={(e) => setCfg({ ...cfg, thankYouMessage: e.target.value })}
              onBlur={(e) => save({ thankYouMessage: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
            />
            <label className="mt-3 flex items-center justify-between gap-3 p-2 rounded-lg border border-[#EFE3D8] hover:bg-[#FBF7F2]">
              <span className="text-sm text-[#2A2A2A]">Show "Getting married? Try Yourweddingfriends" card <span className="text-[11px] text-[#6A6A6A]">(recommended — drives referrals)</span></span>
              <input
                data-testid="rsvp-cfg-referral"
                type="checkbox"
                checked={cfg.showReferralCard !== false}
                onChange={(e) => { setCfg({ ...cfg, showReferralCard: e.target.checked }); save({ showReferralCard: e.target.checked }); }}
                className="accent-[#7B2C3B] w-4 h-4"
              />
            </label>
          </section>

          {/* Confirmation EMAIL — Yes */}
          <ConfirmMessageEditor
            testIdPrefix="rsvp-cfg-conf-email-yes"
            channel="email"
            title='Confirmation email — when a guest ACCEPTS'
            infoTipId="info-conf-email-yes"
            infoTipBody={<><p>Sent by <strong>email</strong> to the guest right after they say Happily Accepts. Only sent if we have their email address.</p><p>Merge fields: <code>{'{firstName}'}</code>, <code>{'{coupleNames}'}</code>, <code>{'{weddingDate}'}</code>, <code>{'{venue}'}</code></p></>}
            subjectValue={cfg.confirmationEmail?.subjectYes || ''}
            bodyValue={cfg.confirmationEmail?.bodyYes || ''}
            onSubjectChange={(v) => setCfg({ ...cfg, confirmationEmail: { ...(cfg.confirmationEmail || {}), subjectYes: v } })}
            onSubjectBlur={(v) => save({ confirmationEmail: { subjectYes: v } })}
            onBodyChange={(v) => setCfg({ ...cfg, confirmationEmail: { ...(cfg.confirmationEmail || {}), bodyYes: v } })}
            onBodyBlur={(v) => save({ confirmationEmail: { bodyYes: v } })}
          />

          {/* Confirmation EMAIL — No */}
          <ConfirmMessageEditor
            testIdPrefix="rsvp-cfg-conf-email-no"
            channel="email"
            title='Confirmation email — when a guest DECLINES'
            infoTipId="info-conf-email-no"
            infoTipBody={<><p>Sent by <strong>email</strong> to the guest right after they say Regretfully Declines.</p></>}
            subjectValue={cfg.confirmationEmail?.subjectNo || ''}
            bodyValue={cfg.confirmationEmail?.bodyNo || ''}
            onSubjectChange={(v) => setCfg({ ...cfg, confirmationEmail: { ...(cfg.confirmationEmail || {}), subjectNo: v } })}
            onSubjectBlur={(v) => save({ confirmationEmail: { subjectNo: v } })}
            onBodyChange={(v) => setCfg({ ...cfg, confirmationEmail: { ...(cfg.confirmationEmail || {}), bodyNo: v } })}
            onBodyBlur={(v) => save({ confirmationEmail: { bodyNo: v } })}
          />

          {/* Confirmation SMS — Yes */}
          <ConfirmMessageEditor
            testIdPrefix="rsvp-cfg-conf-sms-yes"
            channel="sms"
            title='Confirmation SMS — when a guest ACCEPTS'
            infoTipId="info-conf-sms-yes"
            infoTipBody={<><p>Sent by <strong>text message</strong> to the guest right after they say Happily Accepts. Only sent if we have their phone. Keep it short — SMS caps around 160 chars per segment.</p><p>Merge fields: <code>{'{firstName}'}</code>, <code>{'{coupleNames}'}</code>, <code>{'{weddingDate}'}</code>, <code>{'{venue}'}</code></p></>}
            bodyValue={cfg.confirmationSms?.bodyYes || ''}
            onBodyChange={(v) => setCfg({ ...cfg, confirmationSms: { ...(cfg.confirmationSms || {}), bodyYes: v } })}
            onBodyBlur={(v) => save({ confirmationSms: { bodyYes: v } })}
          />

          {/* Confirmation SMS — No */}
          <ConfirmMessageEditor
            testIdPrefix="rsvp-cfg-conf-sms-no"
            channel="sms"
            title='Confirmation SMS — when a guest DECLINES'
            infoTipId="info-conf-sms-no"
            infoTipBody={<p>Sent by <strong>text message</strong> to the guest right after they say Regretfully Declines.</p>}
            bodyValue={cfg.confirmationSms?.bodyNo || ''}
            onBodyChange={(v) => setCfg({ ...cfg, confirmationSms: { ...(cfg.confirmationSms || {}), bodyNo: v } })}
            onBodyBlur={(v) => save({ confirmationSms: { bodyNo: v } })}
          />

          {/* Plus-one name reminder SMS */}
          <ConfirmMessageEditor
            testIdPrefix="rsvp-cfg-plus-one-name-reminder"
            channel="sms"
            title="Plus-one name reminder SMS"
            infoTipId="info-plus-one-reminder"
            infoTipBody={<><p>Sent to guests who said Yes and are bringing a plus-one but haven&apos;t supplied their plus-one&apos;s name yet — you fire it from the <strong>Wedding dashboard</strong> whenever you want to chase names for the seating chart.</p><p>Merge fields: <code>{'{firstName}'}</code>, <code>{'{coupleNames}'}</code>, <code>{'{weddingDate}'}</code>, <code>{'{rsvpShortLink}'}</code></p><p>To avoid pestering guests, each guest is nudged at most once every 7 days.</p></>}
            bodyValue={cfg.plusOneNameReminder?.smsTemplate || ''}
            onBodyChange={(v) => setCfg({ ...cfg, plusOneNameReminder: { ...(cfg.plusOneNameReminder || {}), smsTemplate: v } })}
            onBodyBlur={(v) => save({ plusOneNameReminder: { smsTemplate: v } })}
          />

          {/* RSVP change notifications — SMS to the couple's phones */}
          <UpdateNotificationsEditor cfg={cfg} setCfg={setCfg} save={save} toast={toast} />

          {/* Auto-decline after reply-by */}
          <AutoDeclineEditor cfg={cfg} setCfg={setCfg} save={save} />

          {/* Auto-decline confirmation templates — separate copy that only
              goes out when the system flips a guest to No because reply-by
              passed. Falls back to the standard "No" template when blank. */}
          <section data-testid="auto-decline-templates" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-2 inline-flex items-center gap-2">
              Auto-decline messages
              <InfoTip testId="info-auto-decline-msgs">
                <p>These templates only fire when the <strong>Auto-decline non-responders</strong> feature above triggers. Regular Yes/No confirmations use the templates further up. Leave blank to fall back to the standard No copy.</p>
              </InfoTip>
            </h3>
            <AutoDeclineMessageSwitches cfg={cfg} setCfg={setCfg} save={save} />
            <p className="text-xs text-[#6A6A6A] mb-4">Sent to guests whose RSVP was auto-declined because your reply-by date passed. Guests are declined at your chosen hour; these messages go out when your send window opens (Reminders → Allowed send from). Merge fields: <code>{'{firstName}'}</code>, <code>{'{coupleNames}'}</code>, <code>{'{weddingDate}'}</code>, <code>{'{venue}'}</code>.</p>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#7B2C3B]">Auto-decline email subject</label>
                <input
                  data-testid="ad-email-subject"
                  value={cfg.autoDeclineEmail?.subject || ''}
                  onChange={(e) => setCfg({ ...cfg, autoDeclineEmail: { ...(cfg.autoDeclineEmail || {}), subject: e.target.value } })}
                  onBlur={(e) => save({ autoDeclineEmail: { subject: e.target.value } })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#7B2C3B]">Auto-decline email body</label>
                <textarea
                  data-testid="ad-email-body"
                  rows={5}
                  value={cfg.autoDeclineEmail?.body || ''}
                  onChange={(e) => setCfg({ ...cfg, autoDeclineEmail: { ...(cfg.autoDeclineEmail || {}), body: e.target.value } })}
                  onBlur={(e) => save({ autoDeclineEmail: { body: e.target.value } })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#7B2C3B]">Auto-decline SMS</label>
                <textarea
                  data-testid="ad-sms-body"
                  rows={3}
                  value={cfg.autoDeclineSms?.body || ''}
                  onChange={(e) => setCfg({ ...cfg, autoDeclineSms: { ...(cfg.autoDeclineSms || {}), body: e.target.value } })}
                  onBlur={(e) => save({ autoDeclineSms: { body: e.target.value } })}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]"
                />
              </div>
            </div>
          </section>

          {/* Copy a guest's RSVP link */}
          <section className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
            <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
              <Link2 className="w-4 h-4" /> Copy a guest&apos;s RSVP link
              <InfoTip testId="info-copy-link"><p>Grab one guest&apos;s personal RSVP link to text or email them manually. Each guest has their own private URL — never share one guest&apos;s link with another.</p></InfoTip>
            </h3>
            {guests.length === 0 ? (
              <p className="text-sm text-[#6A6A6A]">Add guests first, then their RSVP links will show up here.</p>
            ) : (
              <>
                {guests.some(g => !g.rsvp_url) && (
                  <button
                    data-testid="rsvp-generate-links"
                    onClick={async () => {
                      setIssuingLinks(true);
                      try {
                        await client.post('/guest-portal/issue-all');
                        const { data } = await client.get('/guests');
                        setGuests(data || []);
                        toast({ title: 'RSVP links generated for every guest' });
                      } catch (e) {
                        toast({ title: 'Could not generate links', description: e?.response?.data?.detail || e.message });
                      } finally {
                        setIssuingLinks(false);
                      }
                    }}
                    className="mb-3 text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-white hover:opacity-90 inline-flex items-center gap-1.5"
                  >
                    {issuingLinks ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Generate RSVP links
                  </button>
                )}
                <div className="max-h-64 overflow-y-auto divide-y divide-[#F4E4DA]">
                  {guests.map((g) => (
                    <div key={g.id} className="py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm text-[#2A2A2A] truncate">{g.name || 'Unnamed'}</p>
                        <p className="text-[10px] text-[#9A8A7E] truncate">{g.rsvp_url ? g.rsvp_url : 'No link yet'}</p>
                      </div>
                      {g.rsvp_url && (
                        <button
                          data-testid={`rsvp-copy-link-${g.id}`}
                          onClick={async () => {
                            await navigator.clipboard.writeText(g.rsvp_url);
                            setCopiedForGuest(g.id);
                            setTimeout(() => setCopiedForGuest(''), 1600);
                          }}
                          className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C] inline-flex items-center gap-1.5 flex-shrink-0"
                        >
                          {copiedForGuest === g.id ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>

        {/* -------- Live preview (right) -------- */}
        <div className="lg:sticky lg:top-6">
          <div className="text-center mb-2">
            <p className="text-[11px] uppercase tracking-wider text-[#9A8A7E]">Guest preview</p>
          </div>
          <PhonePreview cfg={cfg} couple={couple || {}} guest={previewGuest || { plusOne: true }} />
        </div>
      </div>

      {/* RSVP digest reports — full width so the form and its live preview both breathe */}
      <div data-testid="digest-section-fullwidth" className="mt-6">
        <DigestReportsEditor toast={toast} goToProfile={() => navigate('/dashboard?tab=settings#timezone')} />
      </div>
    </div>
  );
}


// Reusable editor for the confirmation email / SMS / reminder templates —
// keeps the RSVP configurator DRY while making the channel of each message
// crystal-clear via the pill under the title.
function ConfirmMessageEditor({ testIdPrefix, channel, title, infoTipId, infoTipBody,
  subjectValue, onSubjectChange, onSubjectBlur,
  bodyValue, onBodyChange, onBodyBlur }) {
  return (
    <section data-testid={testIdPrefix} className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
          {title}
          {infoTipBody && <InfoTip testId={infoTipId}>{infoTipBody}</InfoTip>}
        </h3>
        <ChannelPill channel={channel} />
      </div>
      {onSubjectChange && (
        <div className="mb-3">
          <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Subject line</label>
          <input
            data-testid={`${testIdPrefix}-subject`}
            value={subjectValue}
            onChange={(e) => onSubjectChange(e.target.value)}
            onBlur={(e) => onSubjectBlur(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm"
          />
        </div>
      )}
      <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">{channel === 'sms' ? 'Text message body' : 'Message body'}</label>
      <textarea
        data-testid={`${testIdPrefix}-body`}
        rows={channel === 'sms' ? 3 : 5}
        value={bodyValue}
        onChange={(e) => onBodyChange(e.target.value)}
        onBlur={(e) => onBodyBlur(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm font-sans"
      />
      {channel === 'sms' && (
        <p className="mt-1 text-[10px] text-[#9A8A7E]">{(bodyValue || '').length} chars · SMS segments split at ~160</p>
      )}
    </section>
  );
}



// Master "send these messages" switch + per-channel toggles for auto-decline copy.
function AutoDeclineMessageSwitches({ cfg, setCfg, save }) {
  const ad = cfg.autoDecline || {};
  const on = ad.notifyGuest !== false;
  const setOn = (v) => { setCfg({ ...cfg, autoDecline: { ...ad, notifyGuest: v } }); save({ autoDecline: { notifyGuest: v } }); };
  const setCh = (key, v) => { setCfg({ ...cfg, [key]: { ...(cfg[key] || {}), enabled: v } }); save({ [key]: { enabled: v } }); };
  return (
    <div className="mb-4 space-y-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <input data-testid="auto-decline-notify" type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} className="accent-[#7B2C3B]" />
        <span className="text-sm text-[#2A2A2A]"><strong>Send these messages</strong> to auto-declined guests</span>
      </label>
      {on ? (
        <div className="pl-6 flex gap-4 text-xs text-[#3A3A3A]">
          <label className="inline-flex items-center gap-1.5 cursor-pointer"><input data-testid="auto-decline-email-enabled" type="checkbox" className="accent-[#7B2C3B]" checked={cfg.autoDeclineEmail?.enabled !== false} onChange={(e) => setCh('autoDeclineEmail', e.target.checked)} /> Email</label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer"><input data-testid="auto-decline-sms-enabled" type="checkbox" className="accent-[#7B2C3B]" checked={cfg.autoDeclineSms?.enabled !== false} onChange={(e) => setCh('autoDeclineSms', e.target.checked)} /> Text</label>
          <span className="text-[#9A8A7E]">Text if the guest has a phone, email if they have an email, both if both.</span>
        </div>
      ) : (
        <p data-testid="auto-decline-silent-warning" className="pl-6 text-xs text-[#A15A2A]">Off: guests will be switched to No silently and won't receive anything.</p>
      )}
    </div>
  );
}

// Auto-decline non-responders after the reply-by date passes. Two toggles
// (master + notify-guest) so couples can quietly tidy up their dashboard
// without shipping "sorry we'll miss you" to relatives if they want.
function AutoDeclineEditor({ cfg, setCfg, save }) {
  const ad = cfg.autoDecline || { enabled: false, notifyGuest: true, hour: 1 };
  const setAd = (patch) => {
    const next = { ...ad, ...patch };
    setCfg({ ...cfg, autoDecline: next });
    save({ autoDecline: patch });
  };
  const hour = Number.isFinite(ad.hour) ? ad.hour : 1;
  const fmtHour = (h) => {
    const p = h % 12 === 0 ? 12 : h % 12;
    return `${p}:00 ${h < 12 ? 'AM' : 'PM'}`;
  };
  return (
    <section data-testid="auto-decline-editor" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
      <h3 className="font-serif text-xl text-[#2A2A2A] mb-2 inline-flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" /> Auto-decline non-responders
        <InfoTip testId="info-auto-decline">
          <p>After your reply-by date, guests still marked <strong>Pending</strong> get flipped to <strong>No</strong> automatically — so your headcount and seating chart aren't hostage to unresponsive relatives.</p>
          <p>Runs once, the day after your reply-by date, at the hour you pick below (in your Settings → Timezone). Off by default.</p>
        </InfoTip>
      </h3>
      <p className="text-xs text-[#6A6A6A] mb-3">The day after your reply-by date, every still-pending guest is flipped to <strong>No</strong> at the hour you choose below (in your local timezone). Each auto-declined guest carries a distinct source (<em>"Auto — reply-by passed"</em>) in their audit log so you can always tell manual Nos apart.</p>
      <label className="flex items-center gap-2 cursor-pointer">
        <input data-testid="auto-decline-enabled" type="checkbox" checked={!!ad.enabled} onChange={(e) => setAd({ enabled: e.target.checked })} className="accent-[#7B2C3B]" />
        <span className="text-sm text-[#2A2A2A]"><strong>Enable auto-decline</strong> after reply-by passes</span>
      </label>
      {ad.enabled && (
        <>
          <div className="mt-3 pl-6 flex items-center gap-2 flex-wrap">
            <label htmlFor="auto-decline-hour" className="text-xs text-[#3A3A3A]">Run at</label>
            <select
              id="auto-decline-hour"
              data-testid="auto-decline-hour"
              value={hour}
              onChange={(e) => setAd({ hour: parseInt(e.target.value, 10) })}
              className="text-xs px-2 py-1 rounded-lg border border-[#EFE3D8] bg-white focus:outline-none focus:border-[#7B2C3B]"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{fmtHour(h)}</option>
              ))}
            </select>
            <span className="text-[11px] text-[#9A8A7E]">in your local timezone</span>
          </div>
          <AutoDeclineStatus />
        </>
      )}
    </section>
  );
}

// Editor for the "text me when someone changes their RSVP" feature.
// Accepts a list of phone numbers plus an editable SMS template with a
// live merge-field preview and a one-click "test send" so the couple can
// verify their phones receive it before the first real RSVP lands.
function UpdateNotificationsEditor({ cfg, setCfg, save, toast }) {
  const un = cfg.updateNotifications || { enabled: true, phones: [], smsTemplate: '' };
  const [newPhone, setNewPhone] = React.useState('');
  const [testing, setTesting] = React.useState(false);

  const setUn = (patch) => {
    const next = { ...un, ...patch };
    setCfg({ ...cfg, updateNotifications: next });
    save({ updateNotifications: patch });
  };

  const addPhone = () => {
    const p = (newPhone || '').trim();
    if (!p) return;
    if ((un.phones || []).includes(p)) {
      toast({ title: 'Already in the list' });
      setNewPhone('');
      return;
    }
    const nextPhones = [...(un.phones || []), p];
    setCfg({ ...cfg, updateNotifications: { ...un, phones: nextPhones } });
    save({ updateNotifications: { phones: nextPhones } });
    setNewPhone('');
  };

  const removePhoneAt = (i) => {
    const nextPhones = (un.phones || []).filter((_, idx) => idx !== i);
    setCfg({ ...cfg, updateNotifications: { ...un, phones: nextPhones } });
    save({ updateNotifications: { phones: nextPhones } });
  };

  const testSend = async () => {
    setTesting(true);
    try {
      const { data } = await client.post('/rsvp-notifications/test-send', {});
      toast({
        title: `Test SMS sent to ${data.sent}/${data.total} phone${data.total === 1 ? '' : 's'}`,
        description: data.failures?.length ? `${data.failures.length} failed — check the numbers` : 'Check your phone!',
      });
    } catch (e) {
      toast({ title: 'Test send failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <section data-testid="rsvp-cfg-update-notifications" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h3 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
          RSVP change notifications
          <InfoTip testId="info-update-notifications">
            <p>Whenever any guest submits or updates their RSVP, we text every phone number below so you hear about the change instantly — great for last-minute Yes → No flips.</p>
            <p>Fires on: first-time reply, Yes → No, and No → Yes. Doesn&apos;t fire when a guest only edits their meal, address, or plus-one name.</p>
            <p>Merge fields: <code>{'{guestName}'}</code>, <code>{'{previousReply}'}</code>, <code>{'{newReply}'}</code>, <code>{'{coupleNames}'}</code>, <code>{'{headcountDelta}'}</code>, <code>{'{via}'}</code> (how the reply came in, e.g. guest replied / logged by you; added at the end if you don't use it). Auto-declines arrive as one labeled summary text per run, at the start of your send window.</p>
          </InfoTip>
        </h3>
        <ChannelPill channel="sms" />
        <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#4A4A4A]">
          <input
            data-testid="rsvp-cfg-update-notif-enabled"
            type="checkbox"
            checked={un.enabled !== false}
            onChange={(e) => setUn({ enabled: e.target.checked })}
            className="accent-[#7B2C3B] w-4 h-4"
          />
          Enabled
        </label>
      </div>

      <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Send to these phones</label>
      <div className="mt-1 mb-3 space-y-2">
        {(un.phones || []).map((p, i) => (
          <div key={p + i} data-testid={`rsvp-cfg-update-notif-phone-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#EFE3D8] bg-[#FBF7F2] text-sm">
            <span className="flex-1 font-mono text-[#2A2A2A]">{p}</span>
            <button
              type="button"
              data-testid={`rsvp-cfg-update-notif-remove-${i}`}
              onClick={() => removePhoneAt(i)}
              className="text-[#B8896C] hover:text-red-600"
              title="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {(un.phones || []).length === 0 && (
          <p className="text-xs text-[#6A6A6A] italic">No phones yet — add one below and we&apos;ll text you the moment a guest replies.</p>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <input
          data-testid="rsvp-cfg-update-notif-new-phone"
          value={newPhone}
          onChange={(e) => setNewPhone(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPhone(); } }}
          placeholder="+1 (555) 123-4567"
          className="flex-1 px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm font-mono"
        />
        <button
          type="button"
          data-testid="rsvp-cfg-update-notif-add"
          onClick={addPhone}
          className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full"
        >
          <Plus className="w-3.5 h-3.5" /> Add phone
        </button>
      </div>

      <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Text message body</label>
      <textarea
        data-testid="rsvp-cfg-update-notif-template"
        rows={3}
        value={un.smsTemplate || ''}
        onChange={(e) => setCfg({ ...cfg, updateNotifications: { ...un, smsTemplate: e.target.value } })}
        onBlur={(e) => save({ updateNotifications: { smsTemplate: e.target.value } })}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] focus:border-[#7B2C3B] focus:outline-none text-sm font-sans"
      />
      <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
        <p className="text-[10px] text-[#9A8A7E]">{(un.smsTemplate || '').length} chars · SMS segments split at ~160</p>
        <button
          type="button"
          data-testid="rsvp-cfg-update-notif-test-send"
          onClick={testSend}
          disabled={testing || (un.phones || []).length === 0}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] disabled:opacity-60"
        >
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
          Send test SMS
        </button>
      </div>
    </section>
  );
}




// ---------- Meal options editor (chip list) ----------
function MealOptionsEditor({ options, onChange }) {
  const toggle = (m) => {
    const next = options.includes(m) ? options.filter((o) => o !== m) : [...options, m];
    if (next.length) onChange(MEAL_PRESETS.filter((x) => next.includes(x)));
  };
  return (
    <div>
      <p className="text-xs text-[#6A6A6A] mb-2">Tap to choose which meals guests can pick. Guests bringing a plus-one choose a meal for them too.</p>
      <div className="flex flex-wrap gap-2">
        {MEAL_PRESETS.map((m) => {
          const on = options.includes(m);
          return (
            <button key={m} type="button" data-testid={`rsvp-cfg-meal-preset-${m.toLowerCase()}`} aria-pressed={on} onClick={() => toggle(m)}
              className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${on ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#6A6A6A] border-[#EFE3D8] hover:border-[#B8896C]'}`}>
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ---------- Live phone-shaped preview ----------
// Purely visual — reproduces the RSVP page structure so the couple sees
// exactly what a guest sees, driven by the cfg being edited.
function PhonePreview({ cfg, couple, guest }) {
  const sections = cfg.sections || {};
  const req = cfg.required || {};
  const emailField = cfg.emailField || { alwaysShow: true, requiredIfMissing: true };
  const coupleName = fmtCouple(couple, 'The couple');
  const first = (couple.partner1 || 'Alex').split(' ')[0];
  const merge = (s = '') => (s || '')
    .replace(/\{firstName\}/g, first)
    .replace(/\{coupleNames\}/g, coupleName);
  // Local-time parse to avoid UTC-midnight rolling back a day in negative-offset TZs.
  const dateLabel = (() => {
    if (!couple.weddingDate) return '';
    const [y, m, d] = couple.weddingDate.split('-').map(Number);
    if (!y || !m || !d) return couple.weddingDate;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  })();

  return (
    <div
      data-testid="rsvp-preview-phone"
      className="mx-auto rounded-[36px] border-[6px] border-[#2A2A2A] bg-[#FBF7F2] overflow-hidden shadow-xl"
      style={{ width: '360px', height: '640px' }}
    >
      <div className="h-full overflow-y-auto text-[13px]">
        <header className="px-4 pt-4 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-[#7B2C3B]" />
            <span className="font-serif text-xs font-semibold">Yourwedding<span className="italic text-[#7B2C3B]">friends</span></span>
          </div>
          <span className="text-[9px] text-[#6A6A6A]">Private RSVP</span>
        </header>

        <div className="px-4 py-4">
          <div className="text-center mb-4">
            <p className="text-[9px] uppercase tracking-wider text-[#B8896C]">Hello, {first}</p>
            <h1 className="font-serif text-2xl mt-1 text-[#2A2A2A]">{coupleName}</h1>
            {dateLabel && <p className="text-[10px] text-[#4A4A4A] mt-1">{dateLabel}</p>}
            {couple.venue && <p className="text-[9px] text-[#6A6A6A] mt-0.5">{couple.venue}</p>}
            {couple.venueAddress && <p className="text-[9px] text-[#7B2C3B] mt-0.5">{couple.venueAddress}</p>}
            {couple.venuePhoto && cfg.showVenuePhoto !== false && (
              <img
                data-testid="rsvp-preview-venue-photo"
                src={couple.venuePhoto}
                alt=""
                className="mt-2 mx-auto w-full aspect-[16/9] object-cover rounded-lg border border-[#EFE3D8]"
              />
            )}
          </div>

          {cfg.welcomeMessage && (
            <p className="text-center text-[11px] text-[#4A4A4A] italic mb-3">{merge(cfg.welcomeMessage)}</p>
          )}

          <div className="rounded-2xl bg-white border border-[#EFE3D8] p-3 space-y-3">
            <div>
              <p className="text-[11px] font-medium mb-1">{cfg.attendanceQuestion || 'Will you be joining us?'}</p>
              <div className="grid grid-cols-2 gap-1">
                {[cfg.attendingLabel || 'Happily Accepts', cfg.decliningLabel || 'Regretfully Declines'].map((label, i) => (
                  <div key={label + i} className={`py-1.5 rounded-lg text-[10px] text-center border ${i === 0 ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#2A2A2A] border-[#EFE3D8]'}`}>{label}</div>
                ))}
              </div>
            </div>

            {sections.plusOne && guest.plusOne && (
              <div className="rounded-lg border border-[#EFE3D8] p-2">
                <p className="text-[11px] text-[#2A2A2A]">{cfg.plusOne?.question || 'Are you bringing a plus one?'}</p>
                {cfg.plusOne?.helpText && (
                  <p className="text-[9px] text-[#6A6A6A] mt-0.5">{cfg.plusOne.helpText}</p>
                )}
                <div className="mt-2 grid grid-cols-2 gap-1">
                  <div className="rounded-full bg-[#7B2C3B] text-white text-[10px] text-center py-1">Yes</div>
                  <div className="rounded-full border border-[#EFE3D8] text-[10px] text-center py-1 text-[#3A3A3A]">No</div>
                </div>
              </div>
            )}

            {sections.meal && cfg.mealMode !== 'preset' && (
              <PreviewField label={<>Meal preference {req.meal && <span className="text-[#7B2C3B]">*required</span>}</>} value={cfg.mealOptions?.[0] || '—'} />
            )}
            {sections.meal && cfg.mealMode === 'preset' && (
              <div className="rounded-lg border border-[#EFE3D8] bg-[#FBF7F2] p-2 text-[10px] italic text-[#4A4A4A]">{cfg.mealPresetNote}</div>
            )}

            {sections.dietary && <PreviewField label={<>Dietary needs / allergies {req.dietary && <span className="text-[#7B2C3B]">*required</span>}</>} placeholder="e.g. gluten-free…" />}

            {emailField.alwaysShow !== false && (
              <PreviewField
                label={<>Email address {emailField.requiredIfMissing !== false && <span className="text-[#7B2C3B]">*required</span>}</>}
                placeholder="you@example.com"
              />
            )}

            {sections.address && <PreviewField label={<>Mailing address {req.address && <span className="text-[#7B2C3B]">*required</span>}</>} placeholder="123 Main St…" />}
            {sections.phone && <PreviewField label={<>Phone {req.phone ? <span className="text-[#7B2C3B]">*required</span> : <span className="text-[#9A8A7E]">(optional)</span>}</>} placeholder="555-555-5555" />}
            {sections.customQuestion && cfg.customQuestion?.label && (
              <PreviewField label={<>{cfg.customQuestion.label} {req.customQuestion && <span className="text-[#7B2C3B]">*required</span>}</>} placeholder={cfg.customQuestion.placeholder} />
            )}
            {sections.notes && <PreviewField label={<>Anything else for the couple? {req.notes && <span className="text-[#7B2C3B]">*required</span>}</>} placeholder="A song, a note…" />}

            <div className="pt-1">
              <div className="w-full rounded-full bg-[#7B2C3B] text-white text-[11px] text-center py-2">{cfg.submitLabel || 'Send my reply'}</div>
            </div>
          </div>

          {cfg.showReferralCard !== false && (
            <div className="mt-3 rounded-2xl border border-[#EFE3D8] p-3 bg-gradient-to-br from-[#F4E4DA] to-[#FBF7F2]">
              <p className="text-[9px] uppercase tracking-wider text-[#7B2C3B]">Shown after RSVP</p>
              <p className="font-serif text-sm mt-0.5">Getting married? Try Yourweddingfriends</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewField({ label, placeholder, value }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-[#6A6A6A]">{label}</p>
      <div className="mt-0.5 h-6 rounded-md border border-[#EFE3D8] bg-white px-2 flex items-center text-[10px] text-[#9A8A7E]">
        {value ?? placeholder ?? ''}
      </div>
    </div>
  );
}
